import ChannelInterface from "@/interfaces/channel.interface";
import { TeamsUsersTree } from "@/types/team.types";
import { User } from "@/types/user.types";
import { UserStatusType } from "@/types/userStatus.type";
import { ChatChannel } from "@/views/modules/messaging-inbox/useChat";
import { Ref } from "@vue/composition-api";
import { uniq } from "lodash";

export const resolveTeamsUsersTree = (
    teams: TeamsUsersTree[],
    users: User[],
    searchQuery?: Ref<string>,
    channel?: ChatChannel | ChannelInterface
) => {
    // @NOTE this prop is one of the differences between the legacy ChannelInterface and ChatChannel.
    const isLegacyChannelInterface = (channel: ChannelInterface | ChatChannel): channel is ChannelInterface =>
        "legacy" in channel;

    // If we pass an channel, we check if the linked teams to that particular channel is also listed in the teams list.
    const teamsWithAccess = teams.filter((team) => {
        if (!channel) {
            return true;
        }

        if (isLegacyChannelInterface(channel)) {
            return channel.teamChannels?.find((teamChannel) => teamChannel.teamId === team.id);
        }

        return channel.teamChannels?.find((teamChannel) => teamChannel.id === team.id);
    });

    // Fetch the user id's from every team and make them as one single array with unique user ids.
    const uniqueTeamUserIds = uniq(
        teamsWithAccess.flatMap((team) => team.teamUsers.flatMap((teamUser) => teamUser.userId))
    );

    // Teams with users tree. This does not include users which are NOT in a team.
    const teamsIncludeUsers = teamsWithAccess
        .map((team) => {
            const teamUsers = team.teamUsers
                .map((teamUser) => {
                    // Note that it's possible that a user is inactivated, so it could not be found.
                    const user = users.find((user) => user.id === teamUser.userId);

                    if (!user) {
                        return;
                    }

                    return {
                        id: user.id,
                        name: user.name,
                        status: user.status,
                        type: "user",
                    };
                })
                .filter((teamUser) => teamUser)
                .filter((teamUser) => {
                    if (searchQuery && searchQuery?.value.length > 0) {
                        return (
                            teamUser?.name.toLowerCase().indexOf(searchQuery.value.toLowerCase()) !== -1 ||
                            team?.name.toLowerCase().indexOf(searchQuery.value.toLowerCase()) !== -1
                        );
                    }

                    return teamUser;
                })
                .sort((userA, userB) => {
                    if (!userA || !userB) {
                        return 0;
                    }

                    if (userA.name < userB.name) {
                        return -1;
                    }
                    if (userA.name > userB.name) {
                        return 1;
                    }

                    return 0;
                });

            if (teamUsers?.length === 0) {
                return;
            }

            return {
                type: "team",
                id: team.id,
                name: team.name,
                color: team.color,
                users: teamUsers,
            };
        })
        .filter((team) => team);

    // Users which are NOT in a team.
    const usersNotInTeam = users
        .filter((user) => !uniqueTeamUserIds.includes(user.id))
        .filter((user) => {
            if (searchQuery && searchQuery.value.length > 0) {
                return user?.name.toLowerCase().indexOf(searchQuery.value.toLowerCase()) !== -1;
            }

            return user;
        })
        .filter((user) => user)
        .filter((user) => {
            if (!channel) {
                return true;
            }

            if (isLegacyChannelInterface(channel)) {
                return channel.userChannels?.find((userChannel) => userChannel.userId === user.id);
            }

            return channel.userChannels?.find((userChannel) => userChannel.id === user.id);
        })
        .map((user) => ({
            id: user.id,
            name: user.name,
            status: user.status,
            type: "user",
        }));

    return {
        teamsTree: teamsIncludeUsers.sort((userA, userB) => {
            if (!userA || !userB) {
                return 0;
            }

            if (userA.name < userB.name) {
                return -1;
            }
            if (userA.name > userB.name) {
                return 1;
            }

            return 0;
        }),
        usersTree: usersNotInTeam.sort((userA, userB) => {
            if (userA.name < userB.name) {
                return -1;
            }
            if (userA.name > userB.name) {
                return 1;
            }

            return 0;
        }),
    };
};

export const resolveUserStatus = (status?: UserStatusType) => {
    if (!status) {
        return "status-offline";
    }

    switch (status) {
        case "unavailable":
            return "status-unavailable";
        case "available":
            return "status-online";
    }

    return "status-offline";
};

export const resolveInitials = (name: string): string | undefined => {
    if (!name) {
        return;
    }

    // Remove everything except alpha numeric and spaces
    name = name.replace(/[^a-z\d ]/gi, "");

    const allInitials = name.match(/\b(\w)/g)?.join(" ");

    const initialFirstName = name.charAt(0);
    const initialLastName = name.charAt(name.lastIndexOf(" ") + 1);

    if (allInitials?.length === 1) {
        return initialFirstName;
    }

    return initialFirstName + initialLastName;
};

export const resolveFirstName = (name: string): string | undefined => {
    return name.split(" ")[0];
};
